import {
    IMutateMerchantWithBasicProfileAction,
    fetchMerchantBasicProfile,
} from "actions/merchant-admin-account-actions";
import { MERCHANT_ADMIN_ACCOUNT } from "constants/action-types";
import { IMerchant, IMerchantBasicProfile } from "constants/merchants";
import { format } from "lib/globalize-util";
import { baseActionEpic, IFetchParams, progressEpic } from "./base-epic";
import { ajax } from "lib/observable";
import { add as addSnackbar } from "actions/snackbars";
import { DATATEST } from "constants/integration";
import { pickGrpcError } from "helpers/epics";

export const fetchMerchantEpic = progressEpic<IFetchParams, IMerchant>(
    MERCHANT_ADMIN_ACCOUNT.MERCHANT.FETCH,
    ({ payload }) => ({
        method: payload.method,
        url: payload.url,
    })
);

export const fetchMerchantBasicProfileEpic = progressEpic<IFetchParams, IMerchantBasicProfile>(
    MERCHANT_ADMIN_ACCOUNT.MERCHANT_BASIC_PROFILE.FETCH,
    ({ payload }) => ({
        method: payload.method,
        url: payload.url,
    })
);

export const mutateMerchantEpic = progressEpic<IFetchParams, IMerchant>(
    MERCHANT_ADMIN_ACCOUNT.MERCHANT.MUTATE,
    ({ payload }) => ({
        method: payload.method,
        url: payload.url,
        body: payload.body,
    }),
    {
        finishActions: () => [
            addSnackbar({
                message: format("Success: Merchant updated"),
                testId: DATATEST.MERCHANT_ADMIN_ACCOUNT.SUCCESS_MERCHANT_UPDATE,
            }),
        ],
        errorActions: () => [
            addSnackbar({
                message: format("Error: Could not update the merchant"),
                testId: DATATEST.MERCHANT_ADMIN_ACCOUNT.ERROR_MERCHANT_UPDATE,
            }),
        ],
    }
);

export const muateMerchantWithBasicProfile = (action$) => {
    return action$
        .ofType(MERCHANT_ADMIN_ACCOUNT.MERCHANT.MUTATE_WITH_BASIC_PROFILE)
        .flatMap(
            ({
                payload: { merchantFetchParams, merchantBasicProfileFetchParams },
            }: IMutateMerchantWithBasicProfileAction) =>
                ajax(merchantFetchParams)
                    .mapTo({ merchantBasicProfileFetchParams })
                    .flatMap(({ merchantBasicProfileFetchParams }) =>
                        ajax(merchantBasicProfileFetchParams)
                            .mapTo(
                                addSnackbar({
                                    message: format("Success: Merchant updated"),
                                    testId: DATATEST.MERCHANT_ADMIN_ACCOUNT.SUCCESS_MERCHANT_UPDATE,
                                })
                            )
                            .catch((error) => [
                                addSnackbar({
                                    message: format("Error while trying to update merchant: |errorMessage|", {
                                        errorMessage: pickGrpcError(error) || format("Unknown"),
                                    }),
                                    testId: DATATEST.MERCHANT_ADMIN_ACCOUNT.ERROR_MERCHANT_UPDATE,
                                }),
                            ])
                    )
                    .catch((error) => [
                        addSnackbar({
                            message: format("Error while trying to update merchant: |errorMessage|", {
                                errorMessage: pickGrpcError(error) || format("Unknown"),
                            }),
                            testId: DATATEST.MERCHANT_ADMIN_ACCOUNT.ERROR_MERCHANT_UPDATE,
                        }),
                    ])
        )
        .catch(() => [
            addSnackbar({
                message: format("Error: Could not update merchant"),
                testId: DATATEST.MERCHANT_ADMIN_ACCOUNT.ERROR_MERCHANT_UPDATE,
            }),
        ]);
};

export const mutateMerchantAttributesEpic = progressEpic<IFetchParams, IMerchantBasicProfile>(
    MERCHANT_ADMIN_ACCOUNT.MERCHANT_BASIC_PROFILE.MUTATE,
    ({ payload }) => ({
        method: payload.method,
        url: payload.url,
        body: payload.body,
    }),
    {
        finishActions: () => [
            addSnackbar({
                message: format("Success: Merchant updated"),
                testId: DATATEST.MERCHANT_ADMIN_ACCOUNT.SUCCESS_MERCHANT_UPDATE,
            }),
        ],
        errorActions: () => [
            addSnackbar({
                message: format("Error: Could not update the merchant"),
                testId: DATATEST.MERCHANT_ADMIN_ACCOUNT.ERROR_MERCHANT_UPDATE,
            }),
        ],
    }
);

export const deactivateMerchantEpic = progressEpic<IFetchParams, never>(
    MERCHANT_ADMIN_ACCOUNT.MERCHANT.DEACTIVATE,
    ({ payload }) => ({
        method: payload.method,
        url: payload.url,
        body: payload.body,
    }),
    {
        errorActions: () => [
            addSnackbar({
                message: format("Error: Could not deactivate the merchant"),
                testId: DATATEST.MERCHANT_ADMIN_ACCOUNT.ERROR_MERCHANT_UPDATE,
            }),
        ],
    }
);

export const activateMerchantEpic = progressEpic<IFetchParams, never>(
    MERCHANT_ADMIN_ACCOUNT.MERCHANT.ACTIVATE,
    ({ payload }) => ({
        method: payload.method,
        url: payload.url,
        body: payload.body,
    }),
    {
        errorActions: () => [
            addSnackbar({
                message: format("Error: Could not activate the merchant"),
                testId: DATATEST.MERCHANT_ADMIN_ACCOUNT.ERROR_MERCHANT_UPDATE,
            }),
        ],
    }
);

export const mutateMerchantLogoEpic = baseActionEpic<IFetchParams, IMerchantBasicProfile>(
    MERCHANT_ADMIN_ACCOUNT.MERCHANT_BASIC_PROFILE.UPDATE_LOGO,
    ({ payload }) => ({
        method: payload.method,
        url: payload.url,
        body: payload.body,
    }),
    (_, action) => [fetchMerchantBasicProfile(action.meta?.merchantId)],
    {
        errorActions: () => [
            addSnackbar({
                message: format("Error: Could not update the merchant"),
                testId: DATATEST.MERCHANT_ADMIN_ACCOUNT.ERROR_MERCHANT_UPDATE,
            }),
        ],
    }
);
